/**
 * AdditionalPartiesBlock
 * ----------------------
 * "+ Beteiligte hinzufügen" — Liste weiterer Personen am Schadenfall:
 * Zeugen, Rechtsanwalt, Werkstatt, Polizei, Halter, Fahrer.
 *
 * Mit Autocomplete: Sobald der Gutachter > 1 Zeichen tippt, lädt der
 * Block Vorschläge aus dem zentralen ``parties``-Verzeichnis (alle bisher
 * erfassten Beteiligten über alle Akten hinweg). Klick auf einen
 * Vorschlag füllt Kontakt + Notiz automatisch ein.
 *
 * Admin/Backoffice sehen zusätzlich einen CSV-Export-Button.
 *
 * Props: { value: Array<{id,party_role,name,contact,note}>, onChange, readOnly }
 */
import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Users, Download, History as HistoryIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const PARTY_ROLES = [
  { v: "zeuge", l: "Zeuge / Zeugin" },
  { v: "rechtsanwalt", l: "Rechtsanwalt" },
  { v: "halter", l: "Halter (Geschädigter)" },
  { v: "fahrer", l: "Fahrer" },
  { v: "werkstatt", l: "Werkstatt" },
  { v: "polizei", l: "Polizei (Vorgang/Aktenzeichen)" },
  { v: "gegner_anwalt", l: "Anwalt der Gegenseite" },
  { v: "gegner_versicherung", l: "Sachbearbeiter Gegnerische Vers." },
  { v: "mietwagen", l: "Mietwagenfirma" },
  { v: "abschleppdienst", l: "Abschleppdienst" },
  { v: "sonstige", l: "Sonstige" },
];

const newParty = () => ({
  id: `tmp-${Math.random().toString(36).slice(2, 10)}`,
  party_role: "zeuge",
  name: "",
  contact: "",
  note: "",
});

export default function AdditionalPartiesBlock({ value = [], onChange, readOnly = false }) {
  const { user } = useAuth();
  const isBO = ["admin", "backoffice"].includes(user?.role);
  // Per Default IMMER eingeklappt — nur der "+ Beteiligte hinzufügen"
  // Button ist sichtbar. Beim Klick öffnet sich das Formular, auch wenn
  // bereits Beteiligte gespeichert sind.
  const [expanded, setExpanded] = useState(false);

  const update = (id, patch) =>
    onChange(value.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const remove = (id) => onChange(value.filter((p) => p.id !== id));
  const add = () => {
    onChange([...value, newParty()]);
    setExpanded(true);
  };

  const handleExport = () => {
    const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    const token = localStorage.getItem("access_token") || "";
    const url = `${backendBase}/api/parties/export.csv?auth=${encodeURIComponent(token)}`;
    // Note: /api/parties/export.csv expects Bearer auth header — wir
    // setzen daher kein ?auth=… query, sondern öffnen via fetch+blob.
    fetch(`${backendBase}/api/parties/export.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        const ts = new Date().toISOString().slice(0, 10).replace(/-/g, "");
        a.download = `Beteiligte_${ts}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(u);
      });
  };

  // Collapsed-Default: Nur der "+ Beteiligte hinzufügen"-Button ist
  // sichtbar — alles andere (Header, Icon, Beschreibung, Form) erscheint
  // erst nach Klick auf den Button.
  if (!expanded) {
    return (
      <div className="flex items-center justify-start" data-testid="parties-block-collapsed">
        {!readOnly && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              // Klick öffnet das Formular. Wenn noch keine Beteiligten
              // existieren, gleich eine leere Zeile hinzufügen — sonst
              // einfach die bestehende Liste aufklappen.
              setExpanded(true);
              if (value.length === 0) {
                onChange([...value, newParty()]);
              }
            }}
            data-testid="parties-add-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Beteiligte hinzufügen
            {value.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-cyan-100 text-cyan-700 text-[10px] font-bold">
                {value.length}
              </span>
            )}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-zinc-200 bg-zinc-50/40 overflow-hidden" data-testid="parties-block">
      <div className="px-4 py-3 flex items-center gap-3 border-b border-zinc-200 bg-white">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="h-9 w-9 rounded-xl brand-gradient-soft flex items-center justify-center shrink-0 hover:opacity-80"
          title={expanded ? "Einklappen" : "Ausklappen"}
          data-testid="parties-toggle"
        >
          <Users className="h-4 w-4 text-cyan-700" />
        </button>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex-1 min-w-0 text-left"
          data-testid="parties-header-toggle"
        >
          <div className="font-display font-semibold text-sm flex items-center gap-2">
            Weitere Beteiligte
            {value.length > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-cyan-100 text-cyan-700 text-[10px] font-bold">
                {value.length}
              </span>
            )}
          </div>
          <div className="text-[11px] text-zinc-500">
            Zeugen · Rechtsanwalt · Werkstatt · Polizei · …
            <span className="hidden sm:inline"> · Autocomplete aus deinen früheren Akten</span>
          </div>
        </button>
        {isBO && expanded && (
          <Button
            type="button" size="sm" variant="outline"
            onClick={handleExport}
            className="h-8 text-xs"
            data-testid="parties-export-btn"
            title="Beteiligten-Verzeichnis als CSV exportieren"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" /> CSV
          </Button>
        )}
        {!readOnly && (
          <Button type="button" size="sm" variant="outline" onClick={add} data-testid="parties-add-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Beteiligte hinzufügen
          </Button>
        )}
      </div>

      {value.length === 0 ? (
        <div className="px-4 py-5 text-center text-xs text-zinc-500">
          Noch keine weiteren Beteiligten erfasst.
          <br />
          Klicke oben rechts auf <strong>+ Beteiligte hinzufügen</strong>, um zu starten.
        </div>
      ) : (
        <div className="bg-white divide-y divide-zinc-100">
          {value.map((p) => (
            <div key={p.id} className="px-4 py-3 grid md:grid-cols-[180px_1fr_1fr_40px] gap-2 items-end" data-testid={`party-row-${p.id}`}>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">Rolle</Label>
                <Select value={p.party_role} onValueChange={(v) => update(p.id, { party_role: v })} disabled={readOnly}>
                  <SelectTrigger className="mt-1.5" data-testid={`party-role-${p.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PARTY_ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <PartyNameField
                row={p}
                onUpdate={(patch) => update(p.id, patch)}
                disabled={readOnly}
              />
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">Kontakt / Notiz</Label>
                <Input className="mt-1.5" value={p.contact} onChange={(e) => update(p.id, { contact: e.target.value })} disabled={readOnly} placeholder="Telefon, E-Mail, Adresse, Aktenzeichen…" data-testid={`party-contact-${p.id}`} />
              </div>
              {!readOnly && (
                <Button type="button" variant="ghost" size="icon" onClick={() => remove(p.id)} className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50 self-end" title="Entfernen" data-testid={`party-del-${p.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * PartyNameField — Name-Eingabe mit Autocomplete-Dropdown.
 *
 * Fetcht beim Tippen (debounced, >=2 Zeichen) Vorschläge aus dem
 * zentralen ``parties``-Verzeichnis. Klick auf einen Vorschlag füllt
 * Kontakt + Notiz autom. mit den dort hinterlegten Werten.
 */
function PartyNameField({ row, onUpdate, disabled }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);

  // Fetch suggestions debounced
  useEffect(() => {
    let cancelled = false;
    if (!row.name || row.name.length < 2 || disabled) {
      // Defer state-clear to next microtask so we don't violate the
      // react-hooks/set-state-in-effect rule (setState directly in effect body).
      Promise.resolve().then(() => {
        if (!cancelled) setSuggestions((cur) => (cur.length === 0 ? cur : []));
      });
      return () => { cancelled = true; };
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/parties/suggest", {
          params: { q: row.name, role: row.party_role, limit: 6 },
        });
        if (cancelled) return;
        const items = (data?.items || []).filter(
          (s) => s.name.toLowerCase() !== (row.name || "").toLowerCase(),
        );
        setSuggestions(items);
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
    };
  }, [row.name, row.party_role, disabled]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (s) => {
    onUpdate({
      name: s.name,
      // Nur überschreiben, wenn das Feld noch leer ist — sonst respektiert
      // die manuelle Eingabe des Users.
      contact: row.contact || s.contact || "",
      note: row.note || s.note || "",
      party_role: s.party_role || row.party_role,
    });
    setOpen(false);
    setActiveIdx(-1);
  };

  const handleKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      pick(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">Name</Label>
      <Input
        className="mt-1.5"
        value={row.name}
        onChange={(e) => { onUpdate({ name: e.target.value }); setOpen(true); setActiveIdx(-1); }}
        onFocus={() => row.name.length >= 2 && setOpen(true)}
        onKeyDown={handleKey}
        disabled={disabled}
        placeholder="Vor- und Nachname / Kanzleiname"
        data-testid={`party-name-${row.id}`}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && !disabled && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 rounded-lg border border-zinc-200 bg-white shadow-lg max-h-64 overflow-auto"
          data-testid={`party-name-suggest-${row.id}`}
        >
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold bg-zinc-50/50 border-b border-zinc-100 flex items-center gap-1.5">
            <HistoryIcon className="h-3 w-3" />
            Aus früheren Akten ({suggestions.length})
          </div>
          {suggestions.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => pick(s)}
              onMouseEnter={() => setActiveIdx(i)}
              className={`w-full text-left px-3 py-2 hover:bg-cyan-50 transition flex items-start gap-2 ${
                activeIdx === i ? "bg-cyan-50" : ""
              }`}
              data-testid={`party-suggest-${row.id}-${i}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-zinc-900 truncate">{s.name}</div>
                <div className="text-[11px] text-zinc-500 truncate">
                  <span className="text-cyan-700 font-medium">{(PARTY_ROLES.find((r) => r.v === s.party_role) || {}).l || s.party_role}</span>
                  {s.contact && <> · {s.contact}</>}
                </div>
              </div>
              {s.usage_count > 1 && (
                <span className="text-[10px] text-zinc-500 bg-zinc-100 rounded px-1.5 py-0.5 shrink-0 mt-0.5">
                  {s.usage_count}×
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
